import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Save, Loader2, Plus, Trash2 } from 'lucide-react';
import { db } from '@/lib/firebase/config';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { useToast } from '@/hooks/use-toast';

type BilingualField = { ar: string; en: string };

interface QuickLink {
  text: BilingualField;
  url: string;
}

interface SocialLink {
  platform: string;
  url: string;
}

interface FooterData {
  logo: string;
  logoAlt: BilingualField;
  tagline: BilingualField;
  quickLinks: {
    title: BilingualField;
    links: QuickLink[];
  };
  contact: {
    title: BilingualField;
    phone: string;
    email: string;
    address: BilingualField;
  };
  social: {
    title: BilingualField;
    links: SocialLink[];
  };
  copyright: BilingualField;
}

const defaultFooterData: FooterData = {
  logo: '',
  logoAlt: { ar: '', en: '' },
  tagline: { ar: '', en: '' },
  quickLinks: {
    title: { ar: '', en: '' },
    links: [],
  },
  contact: {
    title: { ar: '', en: '' },
    phone: '',
    email: '',
    address: { ar: '', en: '' },
  },
  social: {
    title: { ar: '', en: '' },
    links: [],
  },
  copyright: { ar: '', en: '' },
};

function toBilingual(val: any): BilingualField {
  if (!val) return { ar: '', en: '' };
  if (typeof val === 'string') return { ar: val, en: '' };
  return { ar: val.ar || '', en: val.en || '' };
}

export function FooterSection() {
  const [footerData, setFooterData] = useState<FooterData>(defaultFooterData);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchFooterData();
  }, []);

  const fetchFooterData = async () => {
    try {
      const docRef = doc(db, 'landing_footer', 'info');
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        setFooterData({
          logo: data.logo || '',
          logoAlt: toBilingual(data.logoAlt),
          tagline: toBilingual(data.tagline),
          quickLinks: {
            title: toBilingual(data.quickLinks?.title),
            links: (data.quickLinks?.links || []).map((link: any) => ({
              text: toBilingual(link.text),
              url: link.url || '',
            })),
          },
          contact: {
            title: toBilingual(data.contact?.title),
            phone: data.contact?.phone || '',
            email: data.contact?.email || '',
            address: toBilingual(data.contact?.address),
          },
          social: {
            title: toBilingual(data.social?.title),
            links: data.social?.links || [],
          },
          copyright: toBilingual(data.copyright),
        });
      }
    } catch (error) {
      console.error('Error fetching footer data:', error);
      toast({ title: 'خطأ', description: 'حدث خطأ أثناء تحميل البيانات', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const docRef = doc(db, 'landing_footer', 'info');
      await setDoc(docRef, { ...footerData, updatedAt: new Date() });
      toast({ title: 'تم الحفظ', description: 'تم حفظ بيانات التذييل بنجاح' });
    } catch (error) {
      console.error('Error saving footer data:', error);
      toast({ title: 'خطأ', description: 'حدث خطأ أثناء حفظ البيانات', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const addQuickLink = () => {
    setFooterData({
      ...footerData,
      quickLinks: {
        ...footerData.quickLinks,
        links: [...footerData.quickLinks.links, { text: { ar: '', en: '' }, url: '' }],
      },
    });
  };

  const removeQuickLink = (index: number) => {
    setFooterData({
      ...footerData,
      quickLinks: {
        ...footerData.quickLinks,
        links: footerData.quickLinks.links.filter((_, i) => i !== index),
      },
    });
  };

  const updateQuickLink = (index: number, field: 'text' | 'url', value: any) => {
    const updated = [...footerData.quickLinks.links];
    updated[index] = { ...updated[index], [field]: value };
    setFooterData({
      ...footerData,
      quickLinks: { ...footerData.quickLinks, links: updated },
    });
  };

  const addSocialLink = () => {
    setFooterData({
      ...footerData,
      social: {
        ...footerData.social,
        links: [...footerData.social.links, { platform: '', url: '' }],
      },
    });
  };

  const removeSocialLink = (index: number) => {
    setFooterData({
      ...footerData,
      social: {
        ...footerData.social,
        links: footerData.social.links.filter((_, i) => i !== index),
      },
    });
  };

  const updateSocialLink = (index: number, field: 'platform' | 'url', value: string) => {
    const updated = [...footerData.social.links];
    updated[index] = { ...updated[index], [field]: value };
    setFooterData({
      ...footerData,
      social: { ...footerData.social, links: updated },
    });
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-bold">إدارة التذييل (Footer)</h3>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              جاري الحفظ...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              حفظ التغييرات
            </>
          )}
        </Button>
      </div>

      <div className="space-y-8">
        {/* Logo */}
        <div className="space-y-2">
          <Label htmlFor="logo">رابط الشعار</Label>
          <Input
            id="logo"
            value={footerData.logo}
            onChange={(e) => setFooterData({ ...footerData, logo: e.target.value })}
            placeholder="https://example.com/logo.png"
            dir="ltr"
          />
        </div>

        {/* Logo Alt */}
        <div className="space-y-3">
          <Label className="text-base font-semibold">وصف الشعار / Logo Alt Text</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="logoAlt-ar" className="text-sm text-muted-foreground">عربي</Label>
              <Input id="logoAlt-ar" value={footerData.logoAlt.ar}
                onChange={(e) => setFooterData({ ...footerData, logoAlt: { ...footerData.logoAlt, ar: e.target.value } })}
                placeholder="نمط" dir="rtl" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="logoAlt-en" className="text-sm text-muted-foreground">English</Label>
              <Input id="logoAlt-en" value={footerData.logoAlt.en}
                onChange={(e) => setFooterData({ ...footerData, logoAlt: { ...footerData.logoAlt, en: e.target.value } })}
                placeholder="Namat" dir="ltr" />
            </div>
          </div>
        </div>

        {/* Tagline */}
        <div className="space-y-3">
          <Label className="text-base font-semibold">الشعار / Tagline</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="tagline-ar" className="text-sm text-muted-foreground">عربي</Label>
              <Input id="tagline-ar" value={footerData.tagline.ar}
                onChange={(e) => setFooterData({ ...footerData, tagline: { ...footerData.tagline, ar: e.target.value } })}
                dir="rtl" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tagline-en" className="text-sm text-muted-foreground">English</Label>
              <Input id="tagline-en" value={footerData.tagline.en}
                onChange={(e) => setFooterData({ ...footerData, tagline: { ...footerData.tagline, en: e.target.value } })}
                dir="ltr" />
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="space-y-4 border-t pt-6">
          <div className="flex justify-between items-center">
            <Label className="text-base font-semibold">الروابط السريعة / Quick Links</Label>
            <Button size="sm" variant="outline" onClick={addQuickLink}>
              <Plus className="h-4 w-4 ml-2" /> إضافة رابط
            </Button>
          </div>
          <div className="space-y-3">
            <Label className="text-base font-semibold">عنوان القسم / Section Title</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">عربي</Label>
                <Input value={footerData.quickLinks.title.ar}
                  onChange={(e) => setFooterData({ ...footerData, quickLinks: { ...footerData.quickLinks, title: { ...footerData.quickLinks.title, ar: e.target.value } } })}
                  dir="rtl" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">English</Label>
                <Input value={footerData.quickLinks.title.en}
                  onChange={(e) => setFooterData({ ...footerData, quickLinks: { ...footerData.quickLinks, title: { ...footerData.quickLinks.title, en: e.target.value } } })}
                  dir="ltr" />
              </div>
            </div>
          </div>
          {footerData.quickLinks.links.map((link, index) => (
            <Card key={index} className="p-4">
              <div className="flex items-start gap-2">
                <div className="flex-1 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">النص (عربي)</Label>
                      <Input value={link.text.ar}
                        onChange={(e) => updateQuickLink(index, 'text', { ...link.text, ar: e.target.value })}
                        dir="rtl" placeholder="الرئيسية" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Text (English)</Label>
                      <Input value={link.text.en}
                        onChange={(e) => updateQuickLink(index, 'text', { ...link.text, en: e.target.value })}
                        dir="ltr" placeholder="Home" />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">الرابط / URL</Label>
                    <Input value={link.url}
                      onChange={(e) => updateQuickLink(index, 'url', e.target.value)}
                      dir="ltr" placeholder="https://..." />
                  </div>
                </div>
                <Button size="icon" variant="ghost" onClick={() => removeQuickLink(index)} className="text-red-500 mt-6">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>

        {/* Contact */}
        <div className="space-y-4 border-t pt-6">
          <Label className="text-base font-semibold">التواصل / Contact</Label>
          <div className="space-y-3">
            <Label className="text-sm font-medium">عنوان القسم / Section Title</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">عربي</Label>
                <Input value={footerData.contact.title.ar}
                  onChange={(e) => setFooterData({ ...footerData, contact: { ...footerData.contact, title: { ...footerData.contact.title, ar: e.target.value } } })}
                  dir="rtl" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">English</Label>
                <Input value={footerData.contact.title.en}
                  onChange={(e) => setFooterData({ ...footerData, contact: { ...footerData.contact, title: { ...footerData.contact.title, en: e.target.value } } })}
                  dir="ltr" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>الهاتف</Label>
              <Input value={footerData.contact.phone}
                onChange={(e) => setFooterData({ ...footerData, contact: { ...footerData.contact, phone: e.target.value } })}
                dir="ltr" />
            </div>
            <div className="space-y-2">
              <Label>البريد الإلكتروني</Label>
              <Input value={footerData.contact.email}
                onChange={(e) => setFooterData({ ...footerData, contact: { ...footerData.contact, email: e.target.value } })}
                dir="ltr" />
            </div>
          </div>
          <div className="space-y-3">
            <Label className="text-sm font-medium">العنوان / Address</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">عربي</Label>
                <Input value={footerData.contact.address.ar}
                  onChange={(e) => setFooterData({ ...footerData, contact: { ...footerData.contact, address: { ...footerData.contact.address, ar: e.target.value } } })}
                  dir="rtl" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">English</Label>
                <Input value={footerData.contact.address.en}
                  onChange={(e) => setFooterData({ ...footerData, contact: { ...footerData.contact, address: { ...footerData.contact.address, en: e.target.value } } })}
                  dir="ltr" />
              </div>
            </div>
          </div>
        </div>

        {/* Social Links */}
        <div className="space-y-4 border-t pt-6">
          <div className="flex justify-between items-center">
            <Label className="text-base font-semibold">وسائل التواصل / Social Media</Label>
            <Button size="sm" variant="outline" onClick={addSocialLink}>
              <Plus className="h-4 w-4 ml-2" /> إضافة
            </Button>
          </div>
          <div className="space-y-3">
            <Label className="text-sm font-medium">عنوان القسم / Section Title</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">عربي</Label>
                <Input value={footerData.social.title.ar}
                  onChange={(e) => setFooterData({ ...footerData, social: { ...footerData.social, title: { ...footerData.social.title, ar: e.target.value } } })}
                  dir="rtl" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">English</Label>
                <Input value={footerData.social.title.en}
                  onChange={(e) => setFooterData({ ...footerData, social: { ...footerData.social, title: { ...footerData.social.title, en: e.target.value } } })}
                  dir="ltr" />
              </div>
            </div>
          </div>
          {footerData.social.links.map((link, index) => (
            <div key={index} className="flex gap-3 items-end">
              <div className="flex-1 space-y-1">
                <Label className="text-xs text-muted-foreground">المنصة (twitter, facebook, instagram, youtube, linkedin)</Label>
                <Input value={link.platform}
                  onChange={(e) => updateSocialLink(index, 'platform', e.target.value)}
                  dir="ltr" placeholder="twitter" />
              </div>
              <div className="flex-1 space-y-1">
                <Label className="text-xs text-muted-foreground">الرابط / URL</Label>
                <Input value={link.url}
                  onChange={(e) => updateSocialLink(index, 'url', e.target.value)}
                  dir="ltr" placeholder="https://..." />
              </div>
              <Button size="icon" variant="ghost" onClick={() => removeSocialLink(index)} className="text-red-500">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>

        {/* Copyright */}
        <div className="space-y-3 border-t pt-6">
          <Label className="text-base font-semibold">حقوق النشر / Copyright</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">عربي</Label>
              <Input value={footerData.copyright.ar}
                onChange={(e) => setFooterData({ ...footerData, copyright: { ...footerData.copyright, ar: e.target.value } })}
                dir="rtl" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">English</Label>
              <Input value={footerData.copyright.en}
                onChange={(e) => setFooterData({ ...footerData, copyright: { ...footerData.copyright, en: e.target.value } })}
                dir="ltr" />
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
